import { msg, useMessages } from 'gt-react';
import { MemoryModuleSchema, type MemoryModule } from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Display label for each memory area (where a correction was made and where a
 * learned rule applies). Registered with `msg()`; decode with `useMessages()`.
 */
export const MEMORY_MODULE_LABEL: Record<MemoryModule, string> = {
  oms: msg('Sales orders', { context: 'memory module: where a rule applies' }),
  cash_receipt: msg('Cash receipts', {
    context: 'memory module: where a rule applies',
  }),
  remittance: msg('Remittances', {
    context: 'memory module: where a rule applies',
  }),
};

/** A memory area read off unvalidated config, else `fallback`. */
export function parseMemoryModule(
  value: unknown,
  fallback: MemoryModule,
): MemoryModule {
  const parsed = MemoryModuleSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

/**
 * A memory-area picker, labelled the way rule cards label areas. `options`
 * narrows it to the areas a choice is valid in (every area by default).
 */
export function MemoryModuleSelect({
  value,
  onChange,
  options = MemoryModuleSchema.options,
  disabled,
  ariaLabel,
  triggerClassName,
}: {
  value: MemoryModule;
  onChange: (module: MemoryModule) => void;
  options?: readonly MemoryModule[];
  disabled?: boolean;
  ariaLabel?: string;
  triggerClassName?: string;
}) {
  const m = useMessages();
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => onChange(MemoryModuleSchema.parse(next))}
    >
      <SelectTrigger className={triggerClassName} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {m(MEMORY_MODULE_LABEL[option])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
